import { type FC, type ReactNode, useEffect, useRef } from "react";

import { Loader2 } from "lucide-react";

import { useMobileUploadSession } from "@/shared/hooks/useMobileUploadSession";
import { QrUploadInstructions } from "@/shared/components/QrUploadInstructions";

import { ModalStepLayout } from "./ModalStepLayout";

interface TriageModalQrStepProps {
  title: string;
  description: string;
  sidebar?: ReactNode;
  documentType: string;
  onBack: () => void;
  onFileUploaded: (files: { fileName: string }[]) => void;
  onSessionExpired?: () => void;
}

export const TriageModalQrStep: FC<TriageModalQrStepProps> = ({
  title,
  description,
  sidebar,
  documentType,
  onBack,
  onFileUploaded,
  onSessionExpired,
}) => {
  const { qrSrc, mobileUrl, uploadCount, uploadedFiles, expiryMinutes } = useMobileUploadSession({
    active: true,
    expiryMinutes: 15,
    maxUploads: 1,
    documentType,
  });

  const hasNotified = useRef(false);

  useEffect(() => {
    if (uploadedFiles.length > 0 && !hasNotified.current) {
      hasNotified.current = true;
      const timeout = setTimeout(() => {
        onFileUploaded(uploadedFiles);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [uploadedFiles, onFileUploaded]);

  const hasUploadedRef = useRef(false);
  hasUploadedRef.current = uploadedFiles.length > 0;
  const onSessionExpiredRef = useRef(onSessionExpired);
  onSessionExpiredRef.current = onSessionExpired;

  useEffect(() => {
    if (mobileUrl && onSessionExpiredRef.current) {
      const timeout = setTimeout(
        () => {
          if (!hasUploadedRef.current) onSessionExpiredRef.current?.();
        },
        expiryMinutes * 60 * 1000,
      );
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [mobileUrl, expiryMinutes]);

  return (
    <ModalStepLayout sidebar={sidebar} title={title} description={description} onBack={onBack}>
      {!mobileUrl ? (
        <div className="flex h-[280px] flex-col items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : (
        <div className="hidden md:flex items-center gap-6">
          <div className="shrink-0 overflow-hidden rounded-xl border border-primary">
            <div className="flex w-[250px] items-center justify-center bg-white p-5">
              <img
                src={qrSrc}
                alt="QR code to open the mobile upload page"
                className="h-[210px] w-[210px] object-contain"
              />
            </div>
            <p className="bg-white px-4 pb-4 text-center text-sm text-secondary">
              Link expires in 15 minutes
            </p>
          </div>

          <QrUploadInstructions uploadCount={uploadCount} />
        </div>
      )}
    </ModalStepLayout>
  );
};
